// `chrome-extension.extension-tabs.shortlist.hunter.review.export` — the export panel, which
// fills the review screen's side view rather than replacing it (see `extension.tsx`).
//
// The alert's name seeds the new-list name, and the ATS decides both the word for a candidate
// list and whether the privacy radios exist at all — so this reads the bootstrap the same way
// the screens above it do.
//
// ⚠️ THE EXPORT IS THREE WRITES IN ORDER, NOT ONE, and upstream splits them across two files:
// the panel only emits `OnCandidatesExport` and the LIST component
// (`extension-alert-candidates.component.ng1.ts:1038-1184`) does the work. Collapsed here into
// the route, because our panel is a child route rather than a sibling of a shared service:
//
//   1. `exportToAtsList`     — starts the ATS write, answers a message id, not an outcome
//   2. `exportStatus`        — polled every second until success or 7 seconds pass
//   3. `exportCandidates`    — records INSIDE Woo that the export happened
//
// ⚠️ STEP 3 ONLY RUNS IF STEP 2 SUCCEEDED. Upstream reaches it through
// `cancelExportPollingAndCompleteDecisionData`, which the poll calls only on `success` —
// a timeout stops the spinner and records nothing. Firing it unconditionally would mark
// candidates exported whose profiles never reached the ATS, and the row's "Exported" state is
// what a recruiter uses to avoid sending someone twice.
import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import {
  AlertEntity,
  exportCandidates,
  exportClientContacts,
  exportClientContactsToAtsList,
  exportStatus,
  exportToAtsList,
  fetchViewModel,
  getAlerts,
  getClientContactAutocomplete,
  getShortlistAutocomplete,
  outwardWriteOutcome,
  type ExportListTarget,
  type ExtensionAlertContainer,
} from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import { CandidateExportPanel, type ExportEntity, type ExportSubmitValues, getAtsLabel } from '@woo-ext/ui'

/** How long upstream polls before giving up — `timePassed >= 7`, in seconds. */
const POLL_TIMEOUT_MS = 7000
const POLL_INTERVAL_MS = 1000

export interface ExportPanelRouteProps {
  /**
   * Which product's review screen opened it. The two panels are the SAME template — byte for
   * byte apart from indentation and one method call — so this route serves both, and the only
   * things that follow the product are the noun in the heading, the alerts it reads and where
   * the ✕ goes back to.
   */
  entity?: ExportEntity
}

/** What the review screen hands down; see the `sideView` block in `ShortlistReview`. */
interface SideViewContext {
  checked?: ReadonlyArray<{ id: number; name?: string | null }>
  onExported?: (ids: readonly number[]) => void
}

export function ExportPanelRoute({ entity = 'candidates' }: ExportPanelRouteProps = {}): ReactElement {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const context = useOutletContext<SideViewContext | null>()
  const checked = context?.checked ?? []
  const [atsName, setAtsName] = useState<string | undefined>(undefined)
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  const [containers, setContainers] = useState<ExtensionAlertContainer[] | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  // Cleared on unmount so a poll cannot outlive the panel and call back into a dead tree.
  const live = useRef(true)

  const contacts = entity === 'clientContacts'
  const raw = params.get('alertId')
  const alertId = raw === null ? undefined : Number.parseInt(raw, 10)

  useEffect(() => {
    live.current = true
    fetchViewModel().then(
      (model) => {
        if (!live.current) return
        setAtsName(model.atsName ?? undefined)
        setAtsLabel(getAtsLabel(model))
      },
      () => undefined,
    )
    getAlerts(contacts ? AlertEntity.ClientContacts : AlertEntity.Candidates).then(
      (loaded) => {
        if (live.current) setContainers(loaded)
      },
      () => undefined,
    )
    return () => {
      live.current = false
    }
  }, [contacts])

  const current = containers?.find((container) => container.alert.id === alertId) ?? containers?.[0]

  const close = (): void => {
    const base = contacts
      ? '/chrome-extension/extension/client-contact/review'
      : '/chrome-extension/extension/shortlist/review'
    void navigate(`${base}${alertId === undefined ? '' : `?alertId=${String(alertId)}`}`)
  }

  /**
   * Step 2. Resolves true only on a reported success — a timeout resolves false, which is
   * upstream's "stop the spinner and record nothing".
   */
  const pollUntilDone = async (exportMessageId: number): Promise<boolean> => {
    const started = Date.now()
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
      if (!live.current) return false
      // ⚠️ A FAILED POLL IS NOT A FAILED EXPORT. Upstream has no catch here at all; one lost
      // response must not abandon a write that is still running, so this keeps polling until
      // the deadline rather than resolving false on the first rejection.
      const answer = await exportStatus(exportMessageId).catch(() => undefined)
      if (answer?.success === true) return true
      if (Date.now() - started >= POLL_TIMEOUT_MS) return false
    }
  }

  const onSubmit = (values: ExportSubmitValues): void => {
    const alert = current?.alert
    if (alert === undefined || submitting) return
    const ids = checked.map((entry) => entry.id)
    // The bar cannot open this panel with an empty selection, but a deep link can, and an
    // export of nobody creates an empty list in the customer's ATS.
    if (ids.length === 0) return

    // ⚠️ `description: ''` IS UPSTREAM'S, not an omission — the new-list branch always sends
    // all three fields, and the union in `packages/api` refuses a partial one for exactly
    // that reason.
    const target: ExportListTarget = values.createNewList
      ? { name: values.name, description: '', isPublic: values.isPublic }
      : { listId: values.listId }

    setSubmitting(true)
    setError(undefined)

    const start = contacts
      ? exportClientContactsToAtsList({ clientContactIds: ids, ...target })
      : exportToAtsList({ candidateIds: ids, ...target })

    void start.then(
      async (started) => {
        const ok = await pollUntilDone(started.exportMessageId)
        if (!live.current) return
        if (!ok) {
          // The ATS has not confirmed inside the window. Upstream leaves the screen as it is
          // and records nothing — the write may still land, so claiming failure would be as
          // wrong as claiming success.
          setSubmitting(false)
          close()
          return
        }
        // Step 3, inside Woo. Its own failure must not undo a completed ATS export, so it is
        // reported no more loudly than upstream's unhandled promise.
        await (contacts ? exportClientContacts(alert.id, ids) : exportCandidates(alert.id, ids)).catch(
          () => undefined,
        )
        if (!live.current) return
        setSubmitting(false)
        context?.onExported?.(ids)
        close()
      },
      (failure: unknown) => {
        if (!live.current) return
        setSubmitting(false)
        // ⚠️ A NETWORK FAILURE REPORTS SUCCESS — see `outwardWriteOutcome`. The write is
        // deduped server-side, so a request that never got an answer may well have landed;
        // saying it failed invites a second press and a duplicate list.
        const outcome = outwardWriteOutcome(failure, 'Something went wrong exporting, please try again.')
        if (outcome.report === 'error') setError(outcome.message)
        else close()
      },
    )
  }

  return (
    <CandidateExportPanel
      entity={entity}
      atsName={atsName}
      {...(atsLabel === undefined ? {} : { atsLabel })}
      flexBasis={ExtensionViewWidth.Extended}
      alertName={current?.alert.name ?? ''}
      checked={checked}
      onSearchLists={contacts ? getClientContactAutocomplete : getShortlistAutocomplete}
      onSubmit={onSubmit}
      submitting={submitting}
      {...(error === undefined ? {} : { error })}
      onClose={close}
    />
  )
}
